import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Auth strategy: short-lived access token (1h) + opaque refresh token (30d, single-use, server-rotated).
 * Both live in localStorage for now — see /app/memory/PRD.md for the httpOnly-cookie migration plan.
 * A response interceptor catches 401s, swaps tokens once, and retries the original request.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refresh_token'));
  const [loading, setLoading] = useState(true);

  // Refs let the axios interceptor read the *current* tokens without re-binding on every render.
  const tokenRef = useRef(token);
  const refreshRef = useRef(refreshToken);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { refreshRef.current = refreshToken; }, [refreshToken]);

  const persistTokens = useCallback((newAccess, newRefresh) => {
    if (newAccess) {
      localStorage.setItem('token', newAccess);
      setToken(newAccess);
    }
    if (newRefresh) {
      localStorage.setItem('refresh_token', newRefresh);
      setRefreshToken(newRefresh);
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // ---- Axios interceptor for transparent refresh on 401 ----
  // Single in-flight refresh promise so a burst of 401s coalesces into one rotation.
  const refreshPromiseRef = useRef(null);

  useEffect(() => {
    const refreshOnce = async () => {
      if (refreshPromiseRef.current) return refreshPromiseRef.current;
      const rt = refreshRef.current;
      if (!rt) return null;
      refreshPromiseRef.current = (async () => {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: rt });
          persistTokens(data.token, data.refresh_token);
          return data.token;
        } catch (err) {
          clearSession();
          return null;
        } finally {
          // Tiny delay so concurrent retries hit the same resolved promise
          setTimeout(() => { refreshPromiseRef.current = null; }, 50);
        }
      })();
      return refreshPromiseRef.current;
    };

    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || '';

        // Only retry once, only on 401, and never on the auth endpoints themselves
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/signup') || url.includes('/auth/refresh');
        if (status !== 401 || original?._retry || isAuthEndpoint) {
          return Promise.reject(error);
        }

        const newAccess = await refreshOnce();
        if (!newAccess) return Promise.reject(error);

        original._retry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return axios(original);
      }
    );
    return () => { axios.interceptors.response.eject(id); };
  }, [persistTokens, clearSession]);

  // Fetch user data when token changes
  useEffect(() => {
    let active = true;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (active) setUser(response.data);
      } catch {
        // 401 here means even the refresh failed — interceptor already cleared session
        if (process.env.NODE_ENV !== 'production') console.debug('[auth] /me lookup failed after refresh');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchUser();
    return () => { active = false; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;
    persistTokens(newToken, newRefresh);
    setUser(userData);
    return userData;
  }, [persistTokens]);

  const signup = useCallback(async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API}/auth/signup`, { email, password, name, role });
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;
    persistTokens(newToken, newRefresh);
    setUser(userData);
    return userData;
  }, [persistTokens]);

  const logout = useCallback(async () => {
    const rt = refreshRef.current;
    if (rt) {
      // Best-effort server revoke; don't block the UI if it fails
      try {
        await axios.post(`${API}/auth/logout`, { refresh_token: rt });
      } catch (err) {
        console.warn('logout revoke failed', err);
      }
    }
    clearSession();
  }, [clearSession]);

  // Allow self-serve profile screens to push fresh user data into context after a save
  const updateUser = useCallback((partial) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    signup,
    logout,
    updateUser,
  }), [user, token, loading, login, signup, logout, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
